import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ComplianceItemView } from '../../../services/ComplianceService/types/complianceTypes';
import { createDefaultComplianceItem } from '../../../services/ComplianceService/createDefaultComplianceItem';
import { complianceQueriesApi } from '../../../services/ComplianceService/complianceQueries';
import { complianceMutationsApi } from '../../../services/ComplianceService/complianceMutations';

interface ComplianceState {
  currentComplianceItem: ComplianceItemView | null;
  selectedComplianceItemId: string | null;
  isEdited: boolean;
  editedFields: string[];
  filters: {
    status: string | null;
    center: string | null;
    complianceType: string | null;
    searchTerm: string;
  };
}

const initialState: ComplianceState = {
  currentComplianceItem: null,
  selectedComplianceItemId: null,
  isEdited: false,
  editedFields: [],
  filters: {
    status: null,
    center: null,
    complianceType: null,
    searchTerm: '',
  },
};

const addEditedField = (state: ComplianceState, fieldName: string) => {
  if (!state.editedFields.includes(fieldName)) {
    state.editedFields.push(fieldName);
  }
  state.isEdited = true;
};

/** Merges only the defined fields from a partial payload onto a draft object (Immer-safe). */
const applyDefinedFields = <T extends object>(target: T, updates: Partial<T>) => {
  Object.entries(updates).forEach(([key, value]) => {
    if (value !== undefined) {
      (target as Record<string, unknown>)[key] = value;
    }
  });
};

const complianceSlice = createSlice({
  name: 'compliance',
  initialState,
  reducers: {
    updateComplianceItem: (state, action: PayloadAction<Partial<ComplianceItemView>>) => {
      if (state.currentComplianceItem) {
        state.currentComplianceItem = { ...state.currentComplianceItem, ...action.payload };
        Object.keys(action.payload).forEach(field => {
          if (!state.editedFields.includes(field)) {
            state.editedFields.push(field);
          }
        });
        state.isEdited = true;
      }
    },

    initializeComplianceItem: (state, action: PayloadAction<string | undefined>) => {
      state.currentComplianceItem = createDefaultComplianceItem(action.payload);
      state.isEdited = false;
      state.editedFields = [];
    },

    clearComplianceItem: (state) => {
      state.currentComplianceItem = null;
      state.selectedComplianceItemId = null;
      state.isEdited = false;
      state.editedFields = [];
    },

    setSelectedComplianceItemId: (state, action: PayloadAction<string | null>) => {
      state.selectedComplianceItemId = action.payload;
    },

    // ── Identification ─────────────────────────────────────────────────
    updateComplianceIdentification: (state, action: PayloadAction<{
      title?: string;
      description?: string;
      complianceType?: string;
      accreditationBody?: string;
      standard?: string;
      requirement?: string;
      category?: string;
    }>) => {
      if (state.currentComplianceItem) {
        applyDefinedFields(state.currentComplianceItem, action.payload);
        addEditedField(state, 'identification');
      }
    },

    // ── Facility ───────────────────────────────────────────────────────
    updateComplianceFacility: (state, action: PayloadAction<{ center?: string }>) => {
      if (state.currentComplianceItem) {
        const updates = action.payload;
        if (updates.center !== undefined) state.currentComplianceItem.center = updates.center;
        addEditedField(state, 'facility');
      }
    },

    // ── Schedule ───────────────────────────────────────────────────────
    updateComplianceSchedule: (state, action: PayloadAction<{
      frequencyRequired?: string;
      nextDueDate?: string;
      reminderDays?: number;
    }>) => {
      if (state.currentComplianceItem) {
        const updates = action.payload;
        if (updates.frequencyRequired !== undefined) state.currentComplianceItem.frequencyRequired = updates.frequencyRequired;
        if (updates.nextDueDate !== undefined) state.currentComplianceItem.nextDueDate = updates.nextDueDate;
        if (updates.reminderDays !== undefined) state.currentComplianceItem.reminderDays = updates.reminderDays;
        addEditedField(state, 'schedule');
      }
    },

    // ── Notes ──────────────────────────────────────────────────────────
    updateComplianceNotes: (state, action: PayloadAction<{ notes?: string }>) => {
      if (state.currentComplianceItem) {
        const updates = action.payload;
        if (updates.notes !== undefined) state.currentComplianceItem.notes = updates.notes;
        addEditedField(state, 'notes');
      }
    },

    // ── Filters ────────────────────────────────────────────────────────
    setStatusFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.status = action.payload;
    },
    setCenterFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.center = action.payload;
    },
    setComplianceTypeFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.complianceType = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.filters.searchTerm = action.payload;
    },
    clearFilters: (state) => {
      state.filters = { status: null, center: null, complianceType: null, searchTerm: '' };
    },

    // ── General ────────────────────────────────────────────────────────
    resetComplianceItem: (state) => {
      state.currentComplianceItem = null;
      state.selectedComplianceItemId = null;
      state.isEdited = false;
      state.editedFields = [];
    },
    markAsSaved: (state) => { state.isEdited = false; state.editedFields = []; },
    discardChanges: (state) => { state.isEdited = false; state.editedFields = []; },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        complianceQueriesApi.endpoints.getComplianceItemById.matchFulfilled,
        (state, action) => {
          state.currentComplianceItem = action.payload;
          state.selectedComplianceItemId = action.payload.id;
          state.isEdited = false;
          state.editedFields = [];
        }
      )
      .addMatcher(
        complianceMutationsApi.endpoints.upsertComplianceItem.matchFulfilled,
        (state, action) => {
          state.currentComplianceItem = action.payload;
          if (action.payload.id) {
            state.selectedComplianceItemId = action.payload.id;
          }
          state.isEdited = false;
          state.editedFields = [];
        }
      );
  },
});

export const {
  updateComplianceItem,
  initializeComplianceItem,
  clearComplianceItem,
  setSelectedComplianceItemId,
  updateComplianceIdentification,
  updateComplianceFacility,
  updateComplianceSchedule,
  updateComplianceNotes,
  setStatusFilter,
  setCenterFilter,
  setComplianceTypeFilter,
  setSearchTerm,
  clearFilters,
  resetComplianceItem,
  markAsSaved,
  discardChanges,
} = complianceSlice.actions;

export const complianceReducer = complianceSlice.reducer;
export default complianceReducer;
